import { useQuery } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import FormField from '@/components/Forms/FormField';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { selectTeamTestingProperties } from '@/selectors';
import { TestingAudienceResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';
import { getAudienceSegments } from '@/utils/optimisationTests';
import { Dispatch, RootState } from '@/utils/store';

import TestingChannelsSelectionField from './TestingChannelsSelectionField';
import ToggableCardHeading from './ToggableCardHeading';
import { FormLayout } from './YoutubeProperties';

const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
`;

const Content = styled.section`
  padding: 24px;
  border-top: solid 1px #f8f8f8;
`;

export const EytvSwitchRow = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const StyledControls = styled(Controls)`
  margin-left: 50px;
  flex-shrink: 0;
`;

const Form = styled.form`
  margin: 40px 0;
`;

interface FormDataT {
  testing_audience_id?: number | null;
  eytv_testing_ga_account_id?: string;
  eytv_testing_audience_segment?: string;
  is_eytv_enabled: boolean;
  channels: number[];
}

const EytvProperties = () => {
  const alerts = useAlerts();

  const [showContent, setShowContent] = useState(false);

  const dispatch = useDispatch<Dispatch>();

  const { teamId } = useParams();

  const {
    eytvTestingAudience,
    eytvTestingGaAccountId,
    eytvTestingAudienceSegment,
    testingChannels,
    isEytvEnabled,
  } = useSelector((state: RootState) =>
    selectTeamTestingProperties(state, teamId!),
  );

  const { data: testingAudiences } = useQuery(
    ['testing_audiences'],
    () => api.get<never, TestingAudienceResource[]>('/v1/testing-audiences'),
    {
      staleTime: 120 * 1000,
      select: (data) => [
        { label: 'No Testing Audience', value: null },
        ...data.map((c) => ({ label: c.name, value: c.id })),
      ],
    },
  );

  const { data: googleAdsAccounts } = useQuery(
    ['google_ads_channels'],
    () => api.get<never, { id: number; name: string }[]>('/v1/ga-accounts'),
    {
      staleTime: 120 * 1000,
      select: (data) =>
        data?.map((account) => {
          return {
            label: account.name,
            value: account.id.toString(),
          };
        }) ?? [],
    },
  );

  const {
    handleSubmit,
    clearErrors,
    setError,
    watch,
    control,
    setValue,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      is_eytv_enabled: isEytvEnabled,
      testing_audience_id: eytvTestingAudience,
      eytv_testing_ga_account_id: eytvTestingGaAccountId ?? '',
      eytv_testing_audience_segment: eytvTestingAudienceSegment ?? '',
      channels:
        testingChannels?.filter((c) => c.type === 'eytv')?.map((c) => c.id) ??
        [],
    },
  });

  const isEytvOn = watch('is_eytv_enabled');

  const gaAccountId = watch('eytv_testing_ga_account_id');

  const audienceSegments = getAudienceSegments(gaAccountId ?? '');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const properties = await api.put(
          `/v1/teams/${teamId}/eytv-testing-settings`,
          {
            ...data,
          },
        );

        dispatch.teams.updateTeamTestingProperties({
          properties,
          teamId: parseInt(teamId!),
        });

        alerts.success('eYTV testing properties saved');

        reset(data);
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Form onSubmit={onSubmit}>
      <Card>
        <ToggableCardHeading
          setShowContent={setShowContent}
          count={
            testingChannels?.filter((c) => c.type === 'eytv')?.map((c) => c.id)
              .length
          }
          showContent={showContent}
          title="eYTV Properties"
          hasSubmitButton
          isSubmitting={isSubmitting}
          isDirty={isDirty}
        />
        <Content>
          <FormLayout>
            <div>
              <Text $type="smallBold">eYTV Files</Text>
              <EytvSwitchRow>
                <Text $type="smallNormal" $color="gray">
                  Manage CSV file uploads to the eYTV file listing page in
                  specific Team settings.
                </Text>
                <StyledControls>
                  <ControlsButton
                    type="button"
                    active={isEytvOn}
                    onClick={() => {
                      setValue('is_eytv_enabled', true, { shouldDirty: true });
                    }}
                  >
                    On
                  </ControlsButton>
                  <ControlsButton
                    type="button"
                    active={!isEytvOn}
                    gray
                    onClick={() => {
                      setValue('is_eytv_enabled', false, { shouldDirty: true });
                    }}
                  >
                    Off
                  </ControlsButton>
                </StyledControls>
              </EytvSwitchRow>
            </div>
            <div>
              <FormField<FormDataT, string>
                type="select"
                control={control}
                name="eytv_testing_ga_account_id"
                options={googleAdsAccounts ?? []}
                label="Google ads account"
                onChangeCallback={() =>
                  setValue('eytv_testing_audience_segment', '')
                }
              />
            </div>
            <div>
              <FormField<FormDataT, string>
                type="select"
                control={control}
                name="eytv_testing_audience_segment"
                options={audienceSegments ?? []}
                disabled={!gaAccountId}
                label="Audience segment"
              />
            </div>
            <div>
              <FormField<FormDataT, number | null>
                type="select"
                control={control}
                name="testing_audience_id"
                options={
                  testingAudiences ?? [
                    { label: 'No Testing Audience', value: null },
                  ]
                }
                label="Target audience"
              />
            </div>
          </FormLayout>
        </Content>
        {!!showContent && (
          <TestingChannelsSelectionField<FormDataT>
            control={control}
            name="channels"
            label="Channels Options"
          />
        )}
      </Card>
    </Form>
  );
};

export default EytvProperties;
